import React, { useState } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { handleViewProfile, handleDownloadABHACard, handleLinkAbhaAddress } from '../../utils/api';
import { API_BASE } from '../../utils/types';

export const SuccessView: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { userProfile, xToken, authData, authConfig } = state;
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);

    // NEW: Loading state for the "Link to Hospital" action
    const [linkHospitalLoading, setLinkHospitalLoading] = useState(false);

    const onViewProfile = async () => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const profile = await handleViewProfile(authData!, xToken, authConfig);
            dispatch({ type: 'SET_USER_PROFILE', payload: profile });
            alert('Profile details refreshed successfully!');
        } catch (error) {
            const msg = (error as Error).message;
            setOtpError(msg);
            alert('View Profile failed: ' + msg);
        } finally {
            setOtpLoading(false);
        }
    };

    const onDownloadCard = async () => {
        try {
            await handleDownloadABHACard(authData!, userProfile!, xToken, authConfig);
        } catch (error) {
            alert('Could not download ABHA Card. Error: ' + (error as Error).message);
        }
    };

    const onLinkAbha = async () => {
        setLinkLoading(true);
        try {
            const linkResult = await handleLinkAbhaAddress(authData!, userProfile!);
            alert(`ABHA Profile linked successfully! Member ID: ${linkResult.memberId}`);
        } catch (error) {
            alert('Failed to link ABHA Profile: ' + (error as Error).message);
        } finally {
            setLinkLoading(false);
        }
    };

    // NEW: Function to send ABHA data back to the hospital page
    const onLinkToHospital = async () => {
        setLinkHospitalLoading(true);
        try {
            // Ensure we have a profile
            if (!userProfile) {
                throw new Error('No user profile available to link.');
            }

            // Name Parsing Logic from normalized fullName
            let firstName = '';
            let middleName = '';
            let lastName = '';

            if (userProfile.fullName) {
                const parts = userProfile.fullName.trim().split(/\s+/);
                if (parts.length === 1) {
                    firstName = parts[0];
                } else if (parts.length === 2) {
                    firstName = parts[0];
                    lastName = parts[1];
                } else if (parts.length > 2) {
                    firstName = parts[0];
                    lastName = parts[parts.length - 1];
                    middleName = parts.slice(1, parts.length - 1).join(' ');
                }
            }

            // Normalize DOB to ISO format (yyyy-mm-dd) — the hospital's
            // CiftechAbhaApi uses C# DateTime.Parse which expects yyyy-mm-dd.
            // ABDM returns dob in dd-mm-yyyy; sending that format crashes their API with 500.
            let normalizedDob = userProfile.dob || '';
            if (normalizedDob) {
                // dd-mm-yyyy → yyyy-mm-dd
                const ddmmyyyy = normalizedDob.match(/^(\d{2})-(\d{2})-(\d{4})$/);
                if (ddmmyyyy) {
                    normalizedDob = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
                } else {
                    // dd/mm/yyyy → yyyy-mm-dd
                    const ddmmyyyySlash = normalizedDob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                    if (ddmmyyyySlash) {
                        normalizedDob = `${ddmmyyyySlash[3]}-${ddmmyyyySlash[2]}-${ddmmyyyySlash[1]}`;
                    }
                    // already yyyy-mm-dd → leave as-is
                }
            }

            // Construct the request body from normalized UserProfile
            const requestBody = {
                refNo: "ref100",
                abhaNumber: userProfile.abhaNumber || "",
                abhaAddress: userProfile.abhaAddress || "",
                firstName: firstName,
                middleName: middleName,
                lastName: lastName,
                dob: normalizedDob,
                gender: userProfile.gender || "",
                address: userProfile.address || "",
                town: userProfile.town || "",
                district: userProfile.district || "",
                state: userProfile.state || "",
                mobile: userProfile.mobile || "",
                email: userProfile.email || "",
                photoBase64: userProfile.profilePhoto || ""
            };

            console.log('Sending Link Request:', requestBody);

            // Guard: tenant must have a callbackUrl configured (resolved server-side by proxy)
            if (!authData?.callbackUrl) {
                throw new Error('No callback URL configured for this facility. Contact admin.');
            }

            // Route through backend proxy — avoids browser mixed-content + invalid SSL cert blocks.
            // The backend reads tenant.callbackUrl server-side and forwards the payload.
            // Backend RestTemplate trusts all certs, so self-signed hospital certs work too.
            const proxyUrl = `${API_BASE}/api/abdm/api/v1/patients/callback-proxy`;
            console.log('Sending via backend proxy to hospital:', authData.callbackUrl);
            const response = await fetch(proxyUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authData.token}`
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();
            // Backend wraps result in ApiResponse: { success, message, data: { success, httpStatus, responseBody, error } }
            const innerResult = result?.data || result;

            if (response.ok && innerResult?.success === true) {
                alert('ABHA Linked Successfully!');
                console.log('Link API Response:', innerResult);
            } else {
                throw new Error(innerResult?.error || result?.message || 'Unknown API error');
            }

        } catch (error) {
            console.error('Failed to link to hospital:', error);
            alert('Failed to send data to hospital: ' + (error as Error).message);
        } finally {
            setLinkHospitalLoading(false);
        }
    };

    const handleResetFlow = () => {
        dispatch({ type: 'RESET_FLOW', payload: { currentStep: 'authType' } });
    };

    if (!authData || !userProfile) return null;

    return (
        <div className="step-container success-card">
            <div className="success-icon">🎉</div>
            <h2>{authConfig.type === 'enrollment' ? 'ABHA Created Successfully!' : 'ABHA Verified Successfully!'}</h2>

            {/* Profile Display */}
            <div className="abha-profile-card">
                <h4>ABHA Profile</h4>
                <div className="profile-photo-container">
                    {userProfile.profilePhoto ? (
                        <img src={`data:image/jpeg;base64,${userProfile.profilePhoto}`} alt="Profile Photo" className="profile-photo" />
                    ) : (
                        <div className="default-avatar">
                            {(userProfile.fullName || 'A').charAt(0).toUpperCase()}
                        </div>
                    )}
                </div>
                <p><strong>Full Name:</strong> <span>{userProfile.fullName}</span></p>
                <p><strong>ABHA Number:</strong> <span className="abha-number">{userProfile.abhaNumber}</span></p>
                <p><strong>ABHA Address:</strong> <span className="abha-address">{userProfile.abhaAddress}</span></p>
                <p><strong>Date of Birth:</strong> <span>{userProfile.dob || 'N/A'}</span></p>
                <p><strong>Mobile:</strong> <span>{userProfile.mobile}</span></p>
                <p><strong>Address:</strong> <span>{userProfile.address}</span></p>
                <p>
                    <strong>Status:</strong>
                    <span className={`status-${(userProfile.status || '').toLowerCase()}`}>
                        {userProfile.status}
                    </span>
                </p>
                <p>
                    <strong>KYC Status:</strong>
                    <span className={`status-${(userProfile.kycStatus || 'pending').toLowerCase()}`}>
                        {userProfile.kycStatus || 'PENDING'}
                    </span>
                </p>
            </div>

            <div className="action-buttons">
                <button
                    onClick={onViewProfile}
                    disabled={!xToken || otpLoading}
                    className="btn-secondary"
                >
                    {otpLoading ? 'Refreshing...' : '👁️ Refresh Profile'}
                </button>
                <button
                    onClick={onDownloadCard}
                    className="btn-primary"
                    disabled={!xToken || otpLoading}
                >
                    ⬇️ Download ABHA Card
                </button>
                {/* <button
                    onClick={onLinkAbha}
                    className="btn-primary"
                    style={{ backgroundColor: '#28a745' }}
                    disabled={linkLoading}
                >
                    {linkLoading ? '🔗 Linking...' : '🔗 Link ABHA'}
                </button> */}
                {/* NEW: Button to send data back to the hospital's page */}
                <button
                    onClick={onLinkToHospital}
                    className="btn-primary"
                    style={{ backgroundColor: '#FF9800' }} // Orange to distinguish it
                    disabled={linkHospitalLoading}
                >
                    {linkHospitalLoading ? 'Sending...' : '🏥 Link ABHA'}
                </button>
                <button
                    onClick={handleResetFlow}
                    className="btn-outline"
                >
                    🔄 New Flow
                </button>
            </div>
            {otpError && <div className="error-message">Error: {otpError}</div>}
        </div>
    );
};